import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import { useCart } from '../context/CartContext';
import { imageUrl } from '../lib/images';

export default function CartPage() {
  const {
    items,
    updateQuantity,
    removeFromCart,
    clearCart,
    totalItems,
    totalPrice,
  } = useCart();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [orderReceipt, setOrderReceipt] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    delivery_method: 'levering', // 'levering' | 'afhentning'
    address: '',
    zip: '',
    city: '',
    notes: '',
    website_url: '', // Mandatory honeypot (Rule 12)
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (formData.website_url) {
      console.warn('Bot detected via honeypot');
      return;
    }

    if (!formData.name || !formData.email || !formData.phone) {
      setErrorMessage('Udfyld venligst navn, e-mail og telefonnummer.');
      return;
    }

    if (formData.delivery_method === 'levering' && (!formData.address || !formData.zip || !formData.city)) {
      setErrorMessage('Udfyld venligst leveringsadresse, postnummer og by.');
      return;
    }

    if (items.length === 0) {
      setErrorMessage('Din kurv er tom.');
      return;
    }

    setIsSubmitting(true);

    try {
      const orderNumber = `KRO-${Math.floor(100000 + Math.random() * 900000)}`;

      const productsSummary = items
        .map(
          (it, idx) =>
            `${idx + 1}. ${it.name} (${it.variant}) - Antal: ${it.quantity} stk.${
              it.price ? ` - a ${it.price} kr. (Linje: ${it.price * it.quantity} kr.)` : ''
            }`
        )
        .join('\n');

      const deliveryText =
        formData.delivery_method === 'levering'
          ? `Levering til: ${formData.address}, ${formData.zip} ${formData.city}`
          : 'Afhentning hos KROMIX / Forhandler';

      const fullSummary = `BESTILLINGSFORMULAR / WEBSHOP (UDEN BETALING)\nOrdrenummer: ${orderNumber}\n\nKUNDE:\nNavn: ${formData.name}\nEmail: ${formData.email}\nTelefon: ${formData.phone}${
        formData.company ? `\nFirma/CVR: ${formData.company}` : ''
      }\n\nLEVERING:\n${deliveryText}\n\nBESTILTE VARER (${totalItems} stk):\n${productsSummary}\n\nSamlet vejledende beløb: ${
        totalPrice > 0 ? `${totalPrice} DKK inkl. moms` : 'Aftales ved bekræftelse'
      }\n\nBEMÆRKNINGER:\n${formData.notes || 'Ingen bemærkninger angivet.'}`;

      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company || '',
        delivery_method: formData.delivery_method,
        address: formData.address || '',
        zip: formData.zip || '',
        city: formData.city || '',
        notes: formData.notes || '',
        order_number: orderNumber,
        order_total_items: totalItems,
        order_total_price: totalPrice > 0 ? `${totalPrice} kr.` : 'Tilbud/aftales',
        order_summary: fullSummary,
        order_items_json: JSON.stringify(items),
        website_url: formData.website_url || '',
        subject: `Ny bestilling #${orderNumber} fra ${formData.name} (${totalItems} varer)`,
      };

      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Kunne ikke indsende bestillingen. Prøv venligst igen.');
      }

      setOrderReceipt({
        orderNumber,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        items: [...items],
        totalItems,
        totalPrice,
        deliveryMethod: formData.delivery_method,
        deliveryAddress:
          formData.delivery_method === 'levering'
            ? `${formData.address}, ${formData.zip} ${formData.city}`
            : 'Afhentning hos KROMIX',
      });

      clearCart();
    } catch (err) {
      console.error('Bestillingsfejl:', err);
      setErrorMessage(
        err.message || 'Der opstod en fejl under afsendelsen. Ring venligst til os på 75 73 18 61.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SeoHead
        title="Bestilling & Kurv - Webshop uden betaling - KROMIX A/S"
        description="Gennemfør din bestilling af KROMIX foder og tilbehør. Ingen direkte kortbetaling – vi kontakter dig for bekræftelse af levering og betaling."
      />

      {/* Page Header */}
      <section className="bg-[#39463A] text-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-[#DED3C0] mb-2 block">
              Webshop & Bestilling
            </span>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
              Din Bestillingskurv
            </h1>
            <p className="text-sm sm:text-base text-[#FCFAF5]/90 mt-2 leading-relaxed">
              Bestil nemt foder og tilbehør uden betalingskort. Vi pakker dine varer og kontakter dig for at bekræfte levering og afregning.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-12 lg:py-16 bg-[#F4EFE5] min-h-[600px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Order Success View */}
          {orderReceipt ? (
            <div className="max-w-2xl mx-auto bg-[#FCFAF5] rounded-3xl p-8 sm:p-12 border border-[#DED3C0] shadow-xl text-center space-y-6">
              <div className="w-20 h-20 rounded-full bg-[#71806A]/15 text-[#71806A] flex items-center justify-center mx-auto text-4xl">
                <i className="fa-solid fa-circle-check" aria-hidden="true" />
              </div>

              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#71806A]">
                  Bestilling bekræftet
                </span>
                <h2 className="text-3xl font-extrabold text-[#39463A] mt-1">
                  Mange tak for din bestilling!
                </h2>
                <p className="text-sm text-[#39463A]/80 mt-2 max-w-lg mx-auto leading-relaxed">
                  Vi har modtaget din ordre og påbegynder ekspeditionen. Vi kontakter dig snarest på{' '}
                  <strong className="text-[#39463A]">{orderReceipt.phone}</strong> eller{' '}
                  <strong className="text-[#39463A]">{orderReceipt.email}</strong>.
                </p>
              </div>

              {/* Receipt Box */}
              <div className="bg-[#F4EFE5] rounded-2xl p-6 border border-[#DED3C0] text-left text-xs sm:text-sm space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-[#DED3C0]">
                  <span className="font-bold text-[#39463A]">Ordrenummer:</span>
                  <span className="font-mono font-bold text-[#71806A] text-base">
                    {orderReceipt.orderNumber}
                  </span>
                </div>

                <div>
                  <span className="font-bold text-[#39463A] block mb-2">Bestilte varer:</span>
                  <ul className="space-y-2 text-[#39463A]/90">
                    {orderReceipt.items.map((it, idx) => (
                      <li key={idx} className="flex justify-between items-center text-xs">
                        <span>
                          {it.quantity}x {it.name} ({it.variant})
                        </span>
                        {it.price ? (
                          <span className="font-bold">{it.price * it.quantity} kr.</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-3 border-t border-[#DED3C0] flex justify-between">
                  <span className="font-bold text-[#39463A]">Levering:</span>
                  <span className="text-right text-xs text-[#39463A]/90">
                    {orderReceipt.deliveryAddress}
                  </span>
                </div>

                {orderReceipt.totalPrice > 0 && (
                  <div className="flex justify-between font-extrabold text-[#39463A] text-base pt-2 border-t border-[#DED3C0]">
                    <span>Samlet vejledende beløb:</span>
                    <span className="text-[#71806A]">{orderReceipt.totalPrice} kr.</span>
                  </div>
                )}
              </div>

              <div className="p-4 rounded-xl bg-[#FCFAF5] border border-[#DED3C0] text-left text-xs text-[#39463A]/80 leading-relaxed">
                <div className="flex items-center gap-2 font-bold text-[#39463A] mb-1">
                  <i className="fa-solid fa-circle-info text-[#71806A]" aria-hidden="true" />
                  <span>Praktisk information om betaling</span>
                </div>
                Da KROMIX opererer med direkte kundekontakt og engrosrådgivning, afregnes ordren via faktura eller MobilePay i forbindelse med pakning og levering. Har du spørgsmål til ordren, kan du altid ringe på <strong>75 73 18 61</strong>.
              </div>

              <div className="pt-4 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/hundefoder"
                  className="px-6 py-3.5 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-white text-xs font-bold uppercase tracking-wider transition-colors shadow-sm"
                >
                  Se flere produkter
                </Link>
                <Link
                  to="/"
                  className="px-6 py-3.5 rounded-xl bg-[#F4EFE5] border border-[#DED3C0] text-[#39463A] text-xs font-bold uppercase tracking-wider hover:bg-[#DED3C0] transition-colors"
                >
                  Gå til forsiden
                </Link>
              </div>
            </div>
          ) : items.length === 0 ? (
            /* Empty Cart View */
            <div className="max-w-md mx-auto bg-[#FCFAF5] rounded-3xl p-10 border border-[#DED3C0] shadow-sm text-center py-16">
              <div className="w-20 h-20 rounded-full bg-[#F4EFE5] border border-[#DED3C0] flex items-center justify-center text-[#71806A]/50 mx-auto mb-4">
                <i className="fa-light fa-basket-shopping text-3xl" aria-hidden="true" />
              </div>
              <h2 className="text-xl font-bold text-[#39463A]">Din bestillingskurv er tom</h2>
              <p className="text-xs text-[#39463A]/70 mt-2 max-w-sm mx-auto leading-relaxed">
                Du har endnu ikke tilføjet nogen produkter til din kurv. Vælg foder eller tilbehør for at starte en bestilling.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/hundefoder"
                  className="px-6 py-3 rounded-xl bg-[#71806A] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#5C6A56] transition-colors shadow-sm"
                >
                  Udforsk hundefoder
                </Link>
                <Link
                  to="/diverse"
                  className="px-6 py-3 rounded-xl bg-[#F4EFE5] border border-[#DED3C0] text-[#39463A] text-xs font-bold uppercase tracking-wider hover:bg-[#DED3C0] transition-colors"
                >
                  Se tilbehør
                </Link>
              </div>
            </div>
          ) : (
            /* Active Cart & Checkout Form Grid */
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              {/* Left Column: Products in Cart */}
              <div className="lg:col-span-7 bg-[#FCFAF5] rounded-3xl p-6 sm:p-8 border border-[#DED3C0] shadow-sm space-y-6">
                <div className="flex items-center justify-between pb-4 border-b border-[#DED3C0]">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#71806A]">
                      Valgte foderprodukter
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-[#71806A] text-white text-[11px] font-bold">
                      {totalItems} {totalItems === 1 ? 'vare' : 'varer'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={clearCart}
                    className="text-xs font-semibold text-[#B98262] hover:text-[#9A6447] transition-colors flex items-center gap-1 cursor-pointer"
                  >
                    <i className="fa-regular fa-trash-can text-xs" aria-hidden="true" />
                    <span>Tøm kurv</span>
                  </button>
                </div>

                {/* Items List */}
                <ul className="divide-y divide-[#DED3C0]">
                  {items.map((item) => (
                    <li key={item.id} className="py-4 flex gap-4 items-center">
                      <div className="w-20 h-20 shrink-0 rounded-2xl bg-[#F4EFE5] border border-[#DED3C0] p-2 flex items-center justify-center">
                        {item.image ? (
                          <img
                            src={imageUrl(item.image, 'width=180,quality=80,format=auto')}
                            alt={item.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <i className="fa-light fa-box text-2xl text-[#71806A]/40" aria-hidden="true" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="text-sm sm:text-base font-bold text-[#39463A] leading-snug">
                              {item.slug ? (
                                <Link
                                  to={`/produkter/${item.slug}`}
                                  className="hover:text-[#71806A] transition-colors"
                                >
                                  {item.name}
                                </Link>
                              ) : (
                                item.name
                              )}
                            </h3>
                            <p className="text-xs text-[#71806A] font-medium mt-0.5">
                              Variant / Størrelse: <span className="text-[#39463A] font-semibold">{item.variant}</span>
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFromCart(item.id)}
                            className="text-[#B98262] hover:text-[#9A6447] p-1 text-sm cursor-pointer"
                            aria-label={`Fjern ${item.name}`}
                          >
                            <i className="fa-solid fa-xmark" aria-hidden="true" />
                          </button>
                        </div>

                        <div className="flex items-center justify-between mt-3">
                          <div className="inline-flex items-center rounded-xl bg-[#F4EFE5] border border-[#DED3C0] p-0.5">
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="w-8 h-8 flex items-center justify-center text-[#39463A] hover:bg-[#DED3C0]/60 rounded-lg text-xs transition-colors cursor-pointer"
                              aria-label="Formindsk antal"
                            >
                              <i className="fa-solid fa-minus text-[10px]" aria-hidden="true" />
                            </button>
                            <span className="w-10 text-center text-xs font-bold text-[#39463A]">
                              {item.quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center text-[#39463A] hover:bg-[#DED3C0]/60 rounded-lg text-xs transition-colors cursor-pointer"
                              aria-label="Forøg antal"
                            >
                              <i className="fa-solid fa-plus text-[10px]" aria-hidden="true" />
                            </button>
                          </div>

                          <div className="text-right">
                            {item.price ? (
                              <div>
                                <span className="text-base font-extrabold text-[#39463A]">
                                  {item.price * item.quantity} kr.
                                </span>
                                {item.quantity > 1 && (
                                  <span className="block text-[10px] text-[#71806A]">
                                    ({item.price} kr. / stk.)
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs font-semibold text-[#71806A]">
                                Forespørgsel
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Subtotal summary */}
                <div className="pt-4 border-t border-[#DED3C0] space-y-2">
                  <div className="flex justify-between items-center text-xs text-[#39463A]">
                    <span>Varer i alt:</span>
                    <span className="font-bold">{totalItems} stk.</span>
                  </div>
                  {totalPrice > 0 && (
                    <div className="flex justify-between items-center text-lg font-extrabold text-[#39463A] pt-1">
                      <span>Vejledende total:</span>
                      <span className="text-2xl text-[#71806A] font-extrabold">
                        {totalPrice} kr.
                      </span>
                    </div>
                  )}
                  <p className="text-[11px] text-[#39463A]/70">
                    Alle priser er vejledende og bekræftes endeligt sammen med eventuel fragt ved ordrebehandling.
                  </p>
                </div>
              </div>

              {/* Right Column: Checkout Webform */}
              <div className="lg:col-span-5 bg-[#FCFAF5] rounded-3xl p-6 sm:p-8 border border-[#DED3C0] shadow-sm space-y-6">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#71806A] block">
                    Gennemfør bestilling
                  </span>
                  <h2 className="text-xl sm:text-2xl font-extrabold text-[#39463A] mt-1">
                    Leverings- & Kundeinfo
                  </h2>
                  <p className="text-xs text-[#39463A]/75 mt-1">
                    Ingen kortbetaling påkrævet. Vi behandler din ordre og fremsender bekræftelse.
                  </p>
                </div>

                {errorMessage && (
                  <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs flex items-start gap-2">
                    <i className="fa-solid fa-triangle-exclamation text-sm shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                <form onSubmit={handleSubmitOrder} className="space-y-4">
                  {/* Honeypot (Rule 12) */}
                  <div className="sr-only" aria-hidden="true">
                    <label htmlFor="cart_website_url">Website URL</label>
                    <input
                      type="text"
                      id="cart_website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleInputChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  {/* Delivery method selector */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-[#39463A] block">
                      Vælg levering / afhentning *
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData((p) => ({ ...p, delivery_method: 'levering' }))}
                        className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                          formData.delivery_method === 'levering'
                            ? 'bg-[#39463A] text-white border-[#39463A] shadow-xs'
                            : 'bg-[#F4EFE5] text-[#39463A] border-[#DED3C0] hover:border-[#71806A]'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 font-bold text-xs">
                          <i className="fa-solid fa-truck-fast" aria-hidden="true" />
                          <span>Levering</span>
                        </div>
                        <span className="block text-[10px] mt-0.5 opacity-80">
                          Direkte til døren
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setFormData((p) => ({ ...p, delivery_method: 'afhentning' }))}
                        className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                          formData.delivery_method === 'afhentning'
                            ? 'bg-[#39463A] text-white border-[#39463A] shadow-xs'
                            : 'bg-[#F4EFE5] text-[#39463A] border-[#DED3C0] hover:border-[#71806A]'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 font-bold text-xs">
                          <i className="fa-solid fa-warehouse" aria-hidden="true" />
                          <span>Afhentning</span>
                        </div>
                        <span className="block text-[10px] mt-0.5 opacity-80">
                          Hos KROMIX i Give
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Name */}
                  <div>
                    <label htmlFor="cart_name" className="block text-xs font-bold text-[#39463A] mb-1">
                      Fulde navn *
                    </label>
                    <input
                      type="text"
                      id="cart_name"
                      name="name"
                      required
                      placeholder="F.eks. Lene Møller"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all"
                    />
                  </div>

                  {/* Email & Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="cart_email" className="block text-xs font-bold text-[#39463A] mb-1">
                        E-mail *
                      </label>
                      <input
                        type="email"
                        id="cart_email"
                        name="email"
                        required
                        placeholder="navn@domaene.dk"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="cart_phone" className="block text-xs font-bold text-[#39463A] mb-1">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        id="cart_phone"
                        name="phone"
                        required
                        placeholder="F.eks. 75 73 18 61"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all"
                      />
                    </div>
                  </div>

                  {/* Company */}
                  <div>
                    <label htmlFor="cart_company" className="block text-xs font-semibold text-[#39463A] mb-1">
                      Evt. Virksomhed / Kennel / CVR (valgfrit)
                    </label>
                    <input
                      type="text"
                      id="cart_company"
                      name="company"
                      placeholder="Kennel- eller firmanavn"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all"
                    />
                  </div>

                  {/* Delivery address if selected */}
                  {formData.delivery_method === 'levering' && (
                    <div className="space-y-3 pt-1">
                      <div>
                        <label htmlFor="cart_address" className="block text-xs font-bold text-[#39463A] mb-1">
                          Leveringsadresse *
                        </label>
                        <input
                          type="text"
                          id="cart_address"
                          name="address"
                          required={formData.delivery_method === 'levering'}
                          placeholder="Vejnavn og husnummer"
                          value={formData.address}
                          onChange={handleInputChange}
                          className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="col-span-1">
                          <label htmlFor="cart_zip" className="block text-xs font-bold text-[#39463A] mb-1">
                            Postnr *
                          </label>
                          <input
                            type="text"
                            id="cart_zip"
                            name="zip"
                            required={formData.delivery_method === 'levering'}
                            placeholder="7323"
                            value={formData.zip}
                            onChange={handleInputChange}
                            className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all"
                          />
                        </div>
                        <div className="col-span-2">
                          <label htmlFor="cart_city" className="block text-xs font-bold text-[#39463A] mb-1">
                            By *
                          </label>
                          <input
                            type="text"
                            id="cart_city"
                            name="city"
                            required={formData.delivery_method === 'levering'}
                            placeholder="Give"
                            value={formData.city}
                            onChange={handleInputChange}
                            className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  <div>
                    <label htmlFor="cart_notes" className="block text-xs font-semibold text-[#39463A] mb-1">
                      Eventuelle bemærkninger
                    </label>
                    <textarea
                      id="cart_notes"
                      name="notes"
                      rows={3}
                      placeholder="Må stilles i garage, ønsket leveringsdato, spørgsmål til fodersammensætning..."
                      value={formData.notes}
                      onChange={handleInputChange}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#DED3C0] focus:border-[#71806A] focus:ring-2 focus:ring-[#71806A]/20 text-xs text-[#252927] outline-hidden transition-all resize-none"
                    />
                  </div>

                  {/* Submit CTA */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 px-6 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-white font-bold text-xs uppercase tracking-wider transition-all shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer mt-2"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin text-sm" aria-hidden="true" />
                        <span>Sender bestilling...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                        <span>Send bestilling uden betaling</span>
                      </>
                    )}
                  </button>

                  <p className="text-[10px] text-center text-[#39463A]/70 pt-1">
                    Ved at indsende accepterer du, at KROMIX A/S kontakter dig angående din bestilling.
                  </p>
                </form>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
